import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'

interface LiveReading {
  id: number
  infrastructure_object_id: number
  object_name: string
  infrastructure_type: string
  reading_type: string
  reading_type_label: string
  value: number
  timestamp: string
  user_name: string
  username: string
  notes: string
  created_at: string
  quality_score?: number
}

interface LiveReadingsManagementTableProps {
  token: string
}

export default function LiveReadingsManagementTable({ token }: LiveReadingsManagementTableProps) {
  const [readings, setReadings] = useState<LiveReading[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReadings, setSelectedReadings] = useState<Set<number>>(new Set())
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    loadReadings()
  }, [])

  const loadReadings = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/statistics/live-readings/management', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setReadings(data)
      } else {
        console.error('Failed to load live readings')
      }
    } catch (error) {
      console.error('Error loading live readings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedReadings(new Set(readings.map(r => r.id)))
    } else {
      setSelectedReadings(new Set())
    }
  }

  const handleSelectReading = (readingId: number, checked: boolean) => {
    const newSelection = new Set(selectedReadings)
    if (checked) {
      newSelection.add(readingId)
    } else {
      newSelection.delete(readingId)
    }
    setSelectedReadings(newSelection)
  }

  const handleBulkDelete = async () => {
    if (selectedReadings.size === 0) return

    try {
      // Call batch delete endpoint
      const response = await fetch('/api/statistics/live-readings/batch-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reading_ids: Array.from(selectedReadings),
        }),
      })

      if (response.ok) {
        const result = await response.json()
        alert(`Usunięto ${result.deleted_count} odczytów live`)
        loadReadings()
        setSelectedReadings(new Set())
        setShowDeleteConfirm(false)
      } else {
        const error = await response.json()
        alert(`Błąd usuwania: ${error.detail || 'Nieznany błąd'}`)
      }
    } catch (error) {
      console.error('Error deleting readings:', error)
      alert('Wystąpił błąd podczas usuwania odczytów')
    }
  }

  const handleDeleteReading = (reading: LiveReading) => {
    const newSelection = new Set(selectedReadings)
    newSelection.add(reading.id)
    setSelectedReadings(newSelection)
    setShowDeleteConfirm(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2">Ładowanie odczytów live...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with bulk actions */}
      {selectedReadings.size > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-purple-800">
                Wybrano {selectedReadings.size} odczytów live ⚡
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                disabled={selectedReadings.size === 0}
              >
                Usuń zaznaczone
              </button>
              <button
                onClick={() => setSelectedReadings(new Set())}
                className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
              >
                Odznacz wszystkie
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              ⚡ Zarządzanie odczytami Live ({readings.length} sztuk)
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={() => loadReadings()}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                🔄 Odśwież
              </button>
            </div>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Lista wszystkich odczytów live z ostatnich 30 dni. Tylko dla administratorów MASTER_ADMIN.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="relative w-12 px-6 sm:w-16 sm:px-8">
                  <input
                    type="checkbox"
                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 sm:left-6"
                    checked={selectedReadings.size === readings.length && readings.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Obiekt
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Typ odczytu
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wartość
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Użytkownik
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jakość
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {readings.map((reading) => (
                <tr key={reading.id} className={selectedReadings.has(reading.id) ? 'bg-purple-50' : ''}>
                  <td className="relative w-12 px-6 sm:w-16 sm:px-8">
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 sm:left-6"
                      checked={selectedReadings.has(reading.id)}
                      onChange={(e) => handleSelectReading(reading.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{reading.object_name}</div>
                    <div className="text-sm text-gray-500">{reading.infrastructure_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{reading.reading_type_label}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {reading.value.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{format(new Date(reading.timestamp), 'dd.MM.yyyy HH:mm:ss')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{reading.user_name}</div>
                    <div className="text-xs text-gray-400">@{reading.username}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {reading.quality_score !== undefined && reading.quality_score !== null ? (
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        reading.quality_score >= 0.9 ? 'bg-green-100 text-green-800' :
                        reading.quality_score >= 0.7 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {(reading.quality_score * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDeleteReading(reading)}
                      className="text-red-600 hover:text-red-900"
                      title="Usuń"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {readings.length === 0 && (
          <div className="text-center py-8 px-4">
            <div className="text-gray-500">
              Brak odczytów live w systemie lub wystąpił błąd ładowania danych.
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <h3 className="mt-2 text-center text-lg font-medium text-gray-900">
                Potwierdź usunięcie
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 text-center">
                  Czy na pewno chcesz usunąć {selectedReadings.size} zaznaczony(ch) odczytów live? <br />
                  Tej operacji nie można cofnąć.
                </p>
              </div>
              <div className="flex justify-center space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium"
                >
                  Tak, usuń
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
